export function normalizeServerString(input: string | null | undefined): string | null {
  if (!input) {
    return null;
  }

  const normalized = input.replace(/&nbsp;/g, ' ').trim();

  return normalized === '' ? null : normalized;
}

export function defendantIdFromServerLinkTag(link: string | null): number | null {
  const normalized = normalizeServerString(link);

  if (normalized) {
    const defendantRegex = new RegExp("DefendantId=(.+?)'");

    return intFromServerString(normalized.match(defendantRegex)![1]?.trim());
  }

  return null;
}

export function intFromServerString(intString: string | null): number | null {
  const normalized = normalizeServerString(intString);

  if (!normalized) {
    return null;
  }

  return Number.parseInt(normalized);
}

export function getServerLinkTagContent(link: string | null): string | null {
  const normalized = normalizeServerString(link);

  if (normalized) {
    const fileRegex = new RegExp('>(.+?)</');

    return normalized.match(fileRegex)![1]?.trim();
  }

  return null;
}

export function fileTooltipFromServerLinkTag(link: string | null): string | null {
  const normalized = normalizeServerString(link);

  if (normalized) {
    const tooltipRegex = new RegExp("title='(.+?)'");

    return normalized.match(tooltipRegex)![1]?.trim();
  }

  return null;
}

export function caseIdFromServerLinkTag(link: string | null): string | null {
  const normalized = normalizeServerString(link);

  if (normalized) {
    const caseRegex = new RegExp('cid=(.+?)&');

    return normalized.match(caseRegex)![1]?.trim();
  }

  return null;
}

export function booleanFromServerString(input: 'True' | 'False'): boolean {
  return input === 'True';
}

export function removeDelimiters(data: string | null) {
  return !data ? '' : data.replace(/[\W]/g, '');
}

export function stringDateToServerString(date: string | null | undefined): string | null {
  if (!date || isNaN(Date.parse(date))) {
    return null;
  }

  return dateToServerString(new Date(date));
}

export function dateToServerString(date: Date | null | undefined): string | null {
  if (!date) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();

  return `${month}-${day}-${year}`;
}


export function dateFromServerString(dateString: string | null | undefined): Date | null {
  const normalized = normalizeServerString(dateString);

  //console.log(`Got inbound server string ${dateString}`);
  if (!normalized) {
    return null;
  }

  const num = Number(dateString);
  if (Number.isNaN(num)) {
   // console.log(`Assuming MM-dd-yyyy format and parsing...`);

    const [month, day, year] = normalized.slice(0, 10).split('-').map(Number);
   // console.log(`Parsed out month ${month}, day: ${day}, year: ${year}`);

    const res = new Date(year, month - 1, day);
   // console.log(`Turned into date: ${res}`);

    return res;
  } else {
   // console.log('Assuming yyyyMMdd format, input is numeric');

    const year = Math.floor(num / 10000);
    const month = Math.floor((num - year * 10000) / 100);
    const day = Math.floor((num - (year * 10000 + month * 100)));
   // console.log(`Parsed out month ${month}, day: ${day}, year: ${year}`);

    const res = new Date(year, month - 1, day);
   // console.log(`Turned into date: ${res}`);
    return res;
  }
}


export function removeQuotationMarksFromServerString(
  input: string | null | undefined
): string | null {
  if (!input) {
    return null;
  }

  const normalized = input.replace(/['"]+/g, '').trim();

  return normalized === '' ? null : normalized;
}

export function getAge(birthDate: Date): { years: number; months: number; days: number } {
  let currentDate = new Date();

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  const yearDob = birthDate.getFullYear();
  const monthDob = birthDate.getMonth();
  const dateDob = birthDate.getDate();

  let yearAge = currentYear - yearDob;
  let monthAge: number;
  let dateAge: number;

  if (currentMonth >= monthDob) {
    monthAge = currentMonth - monthDob;
  } else {
    yearAge--;
    monthAge = 12 + currentMonth - monthDob;
  }

  if (currentDay >= dateDob) {
    dateAge = currentDay - dateDob;
  } else {
    monthAge--;
    dateAge = 31 + currentDay - dateDob;

    if (monthAge < 0) {
      monthAge = 11;
      yearAge--;
    }
  }

  return {
    years: yearAge,
    months: monthAge,
    days: dateAge,
  };
}

export function getAgeMessage(years: number, months: number, days: number): string {
  let yearsStr = years ? (years > 1 ? `${years} years` : `${years} year`) : '';
  let monthsStr = months ? (months > 1 ? `${months} months` : `${months} month`) : '';
  let daysStr = days ? (days > 1 ? `${days} days` : `${days} day`) : '';

  let separatorComma = yearsStr && monthsStr ? ', ' : '';
  let separatorAnd = (yearsStr || monthsStr) && daysStr ? ' and ' : '';

  return yearsStr || monthsStr || daysStr
    ? `${yearsStr}${separatorComma}${monthsStr}${separatorAnd}${daysStr} old`
    : 'Could not calculate the age!';
}

export function parseCommaSeparatedString(input: string | null | undefined): string[] {
  if (!input) {
    return [];
  }

  return input
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

export function floatFromServerString(floatString: string | null): number | null {
  const normalized = normalizeServerString(floatString);

  if (!normalized) {
    return null;
  }

  return Number.parseFloat(normalized);
}

export function normalizeHtmlString(input: string | null | undefined): string {
  if (!input) return '';
  const div = document.createElement('div');
  div.innerHTML = input;
  return div.textContent?.trim() || '';
}

export function formatFileNumber(fileNumber: { prefix: string; suffix: string }) {
  const prefix = fileNumber?.prefix?.trim();
  const suffix = fileNumber?.suffix?.trim();

  if (!prefix || !suffix) {
    return { display: '', link: '' };
  }

  const display = `File #${prefix}-${suffix}`;
  return { display };
}